// Sort.jsx
import React, {startTransition} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'
import {Box, FormControl, Select, Image, Text, useStyleConfig} from '@chakra-ui/react'
import {getUrl} from '../../../utils/cloudinary-utils'
import {scheduleAfterPaint, scheduleIdleAnalytics} from '../../../utils/common-utils'

const Sort = ({
    sortUrls,
    productSearchResult,
    basePath,
    isMenu,
    defaultSortingOption,
    sortSection,
    brSortingOptions,
    searchParams,
    tagManagerProductFilters
}) => {
    const intl = useIntl()
    const history = useHistory()
    const productListStyles = useStyleConfig('ProductListStyles')
    const sortLabel = intl.formatMessage({
        id: 'product_list.drawer.title.sort_by',
        defaultMessage: 'Sort by'
    })
    const isDesktopInline = sortSection === 'desktop'
    const isMobileDrawer = sortSection === 'mobile-drawer'
    const isFilterStyleSort = isDesktopInline || isMobileDrawer

    // Derive initial basePath without mutating props
    const initialBasePath =
        (defaultSortingOption &&
            basePath &&
            sortUrls?.find((url) => url.includes(searchParams?.sort || defaultSortingOption))) ||
        basePath

    // Current sort extraction
    const currentSort = initialBasePath
        ?.split('?')[1]
        ?.split('&')
        ?.find((param) => param.startsWith('sort='))
    const currentSortValue = currentSort?.split('=')[1]

    // Find the selected sort option
    const selectedValue = sortUrls?.find((item) => {
        const sort = item
            .split('?')[1]
            ?.split('&')
            ?.find((param) => param.startsWith('sort='))
        const sortValue = sort?.split('=')?.[1]
        return sortValue === currentSortValue
    })

    const handleChange = ({target}) => {
        const nextUrl = target.value
        const q = nextUrl?.split('?')?.[1]
        const newSort = new URLSearchParams(q).get('sort')

        window.monetateRef = ''
        try {
            const label = document?.querySelector(`option[value="${nextUrl}"]`)?.innerText
            if (label) {
                localStorage.setItem('plpSort', label)
            }
        } catch {
            /** silent fail */
        }

        scheduleAfterPaint(() => {
            startTransition(() => {
                history.push(nextUrl)
            })
        })

        scheduleIdleAnalytics(() => {
            tagManagerProductFilters?.(null, false, newSort)
        })
    }

    return (
        <FormControl
            aria-label={sortLabel}
            data-testid={`sf-product-list-sort-${sortSection}`}
            className="product-list-sort"
            display="flex"
            alignItems="center"
            {...productListStyles?.body?.sortForm?.control}
            width={{
                base: isMenu ? '100%' : 'fit-content',
                md: isDesktopInline ? 'fit-content' : isMenu ? '100%' : '220px'
            }}
            justifyContent="center"
            sx={
                isFilterStyleSort
                    ? {
                          '&:has(select[data-focus-visible-added])': {
                              outline: 'auto'
                          }
                      }
                    : undefined
            }
            {...(isFilterStyleSort && {
                justifyContent: isDesktopInline ? 'flex-start' : 'space-between',
                border: 'none',
                height: isMobileDrawer ? '44px' : '29px',
                position: 'relative',
                gap: isDesktopInline ? '10px' : '12px'
            })}
        >
            {isFilterStyleSort && (
                <>
                    <Text
                        aria-hidden="true"
                        color="black"
                        fontFamily={isMobileDrawer ? 'body' : 'heading'}
                        fontSize={isMobileDrawer ? '16px' : '18px'}
                        fontWeight="400"
                        lineHeight="29px"
                        whiteSpace="nowrap"
                    >
                        {sortLabel}
                    </Text>
                    <Image
                        aria-hidden="true"
                        alt=""
                        src={getUrl('UIImages/images/down-arrow-new.svg')}
                        width="13px"
                        height="13px"
                        filter={isMobileDrawer ? 'brightness(0)' : undefined}
                    />
                </>
            )}
            <Select
                aria-label={sortLabel}
                value={selectedValue || sortUrls?.[0]}
                onChange={handleChange}
                fontFamily={isMenu ? 'body' : 'heading'}
                fontSize={isMenu ? '16px' : '18px'}
                fontWeight={isMenu ? '300' : '400'}
                rootProps={
                    isFilterStyleSort
                        ? {
                              position: 'absolute',
                              inset: 0,
                              height: '100%',
                              zIndex: 1
                          }
                        : undefined
                }
                {...productListStyles?.body?.sortForm?.selectTag}
                {...(isFilterStyleSort && {
                    height: '100%',
                    opacity: '0',
                    cursor: 'pointer'
                })}
                icon={
                    isFilterStyleSort ? (
                        <Box />
                    ) : (
                        <Image
                            alt="Arrow"
                            src={getUrl('UIImages/images/down-arrow-new.svg')}
                            {...productListStyles?.body?.sortForm?.selectIcon}
                            width="13px"
                            height="13px"
                            marginRight={isMenu ? '8px' : '12px'}
                        />
                    )
                }
            >
                {sortUrls?.map((href, index) => (
                    <option key={href} value={href}>
                        {isMenu
                            ? intl.formatMessage(
                                  {
                                      id: 'product_list.select.sort_by',
                                      defaultMessage: 'Sort by: {sortOption}'
                                  },
                                  {
                                      sortOption:
                                          productSearchResult?.sortingOptions?.[index]?.label ||
                                          brSortingOptions?.[index]?.label
                                  }
                              )
                            : intl.formatMessage(
                                  {
                                      id: 'product_list.select.sort_by_text',
                                      defaultMessage: '{sortOption}'
                                  },
                                  {
                                      sortOption:
                                          productSearchResult?.sortingOptions?.[index]?.label ||
                                          brSortingOptions?.[index]?.label
                                  }
                              )}
                    </option>
                ))}
            </Select>
        </FormControl>
    )
}

Sort.propTypes = {
    sortUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
    productSearchResult: PropTypes.shape({
        sortingOptions: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string
            })
        )
    }),
    basePath: PropTypes.string,
    isMenu: PropTypes.bool,
    defaultSortingOption: PropTypes.string,
    sortSection: PropTypes.string,
    brSortingOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string
        })
    ),
    searchParams: PropTypes.object,
    tagManagerProductFilters: PropTypes.func
}

Sort.defaultProps = {
    productSearchResult: {},
    basePath: '',
    isMenu: false,
    defaultSortingOption: '',
    sortSection: '',
    brSortingOptions: [],
    searchParams: {},
    tagManagerProductFilters: () => {}
}

export default Sort
